/**
 * 清新薄荷模版 - VIP会员页（v4）
 * 
 * 设计核心理念：与红色模版（全宽渐变Hero+3栏套餐）完全不同的布局
 * - 顶部：居中白色卡片式 Hero（非全宽渐变），搭配几何插图
 * - 权益区：大图标的横向3大块展示（非6宫格卡片），视觉焦点突出
 * - 套餐区：居中单一Tab切换式套餐展示（非3栏并排），聚焦决策
 * - 整体更轻量、呼吸感强，区别于红色模版的浓重品牌感
 */

import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import TealNavbar from "@/templates/teal/components/Navbar";
import TealFooter from "@/templates/teal/components/Footer";
import { useAuth } from "@/hooks/useAuth";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { getVipPlans, getSiteSettings } from "@/utils/adminApi";

// ==================== API 层 ====================

interface PaymentConfig {
  alipay_enabled: boolean;
  wechat_enabled: boolean;
  qrcode_enabled: boolean;
  qrcode_priority: boolean;
  alipay_qrcode: string;
  wechat_qrcode: string;
}

async function fetchPaymentConfig(): Promise<PaymentConfig | null> {
  try {
    const token = localStorage.getItem("admin_token") || localStorage.getItem("token");
    const siteId = localStorage.getItem('current_site_id') || '';
    const res = await fetch("/api/admin/settings/payment", {
      method: "GET",
      headers: {
        "Authorization": token ? `Bearer ${token}` : "",
        'X-Site-Id': siteId,
      },
    });
    const data = await res.json();
    if (data.code === 0 && data.data) {
      return {
        alipay_enabled: !!data.data.alipay_enabled,
        wechat_enabled: !!data.data.wechat_enabled,
        qrcode_enabled: !!data.data.qrcode_enabled,
        qrcode_priority: !!data.data.qrcode_priority,
        alipay_qrcode: data.data.alipay_qrcode || "",
        wechat_qrcode: data.data.wechat_qrcode || "",
      };
    }
    return null;
  } catch (e) {
    console.error("❌ VIP: 读取支付配置失败", e);
    return null;
  }
}

// ==================== 类型 ====================

interface VipPlan {
  id: string;
  name: string;
  price: number;
  originalPrice: number;
  duration: string;
  badge: string;
  features: string[];
  duration_months: number;
}

const DEFAULT_VIP_PLANS: VipPlan[] = [
  {
    id: "monthly",
    name: "月度会员",
    price: 29,
    originalPrice: 49,
    duration: "1个月",
    badge: "",
    features: [
      "信息置顶展示 3次/月",
      "发布信息不限条数",
      "专属会员标识",
      "优先客服支持",
    ],
    duration_months: 1,
  },
  {
    id: "quarterly",
    name: "季度会员",
    price: 79,
    originalPrice: 147,
    duration: "3个月",
    badge: "热门",
    features: [
      "信息置顶展示 10次/季",
      "发布信息不限条数",
      "专属会员标识",
      "优先客服支持",
      "数据统计分析",
    ],
    duration_months: 3,
  },
  {
    id: "yearly",
    name: "年度会员",
    price: 199,
    originalPrice: 588,
    duration: "12个月",
    badge: "超值",
    features: [
      "信息置顶展示 不限次数",
      "发布信息不限条数",
      "专属会员标识",
      "专属客服支持",
      "数据统计分析",
      "优先审核通道",
      "品牌推广曝光",
    ],
    duration_months: 12,
  },
];

// 权益亮点 - 3大核心权益（非6宫格）
const VIP_HIGHLIGHTS = [
  {
    icon: "ri-rocket-line",
    title: "置顶曝光",
    desc: "信息优先展示在列表顶部，获得更多浏览量与商机转化",
    color: "from-teal-500 to-emerald-500",
    bgColor: "bg-teal-50",
  },
  {
    icon: "ri-shield-check-line",
    title: "认证标识",
    desc: "专属VIP认证标识，提升企业可信度，让客户更信赖您的服务",
    color: "from-cyan-500 to-teal-600",
    bgColor: "bg-cyan-50",
  },
  {
    icon: "ri-infinity-line",
    title: "无限发布",
    desc: "不限条数发布服务信息，无需担心数量限制，业务随心扩展",
    color: "from-emerald-500 to-teal-500",
    bgColor: "bg-emerald-50",
  },
];

// 次要权益 - 横向标签条
const SUB_BENEFITS = [
  { icon: "ri-bar-chart-line", label: "数据分析" },
  { icon: "ri-customer-service-line", label: "专属客服" },
  { icon: "ri-speed-line", label: "极速审核" },
];

// ==================== 页面组件 ====================

export default function TealVipPage() {
  const { isLoggedIn } = useAuth();
  const { siteConfig } = useSiteConfig();
  const [vipPlans, setVipPlans] = useState<VipPlan[]>(DEFAULT_VIP_PLANS);
  const [selectedPlan, setSelectedPlan] = useState("quarterly");
  const [payMethod, setPayMethod] = useState<"wechat" | "alipay" | "qrcode">("wechat");
  const [showPayModal, setShowPayModal] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [enableVip, setEnableVip] = useState(true);

  const siteDisplayName = siteConfig?.site_name || siteConfig?.name || "商服汇";

  // 加载数据
  useEffect(() => {
    async function loadData() {
      try {
        const [plans, settings, payments] = await Promise.all([
          getVipPlans().catch(() => null),
          getSiteSettings().catch(() => null),
          fetchPaymentConfig(),
        ]);

        if (settings) {
          setEnableVip(settings.enable_vip !== false);
        }

        if (plans && Array.isArray(plans) && plans.length > 0) {
          const formattedPlans = plans.map((p: any) => ({
            id: p.id,
            name: p.name || "VIP套餐",
            price: p.amount || p.price || 0,
            originalPrice: p.original_price || p.originalPrice || 0,
            duration: `${p.duration_months || p.duration || 1}个月`,
            badge: p.is_recommended ? "推荐" : "",
            features: p.benefits || p.features || [],
            duration_months: p.duration_months || 1,
          }));
          setVipPlans(formattedPlans);
          if (formattedPlans.length > 0) {
            setSelectedPlan(formattedPlans[0].id);
          }
        }

        if (payments) {
          setPaymentConfig(payments);
          if (payments.qrcode_enabled && payments.qrcode_priority) {
            setPayMethod("qrcode");
          }
        }
      } catch (e) {
        console.error("加载VIP数据失败:", e);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const currentPlan = vipPlans.find((p) => p.id === selectedPlan);

  const showAlipay = !!paymentConfig?.alipay_qrcode && (!!paymentConfig?.qrcode_enabled || !!paymentConfig?.alipay_enabled);
  const showWechat = !!paymentConfig?.wechat_qrcode && (!!paymentConfig?.qrcode_enabled || !!paymentConfig?.wechat_enabled);
  const showQrcode = showAlipay && showWechat;

  useEffect(() => {
    if (payMethod === "wechat" && !showWechat) {
      setPayMethod(showAlipay ? "alipay" : "qrcode");
    } else if (payMethod === "alipay" && !showAlipay) {
      setPayMethod(showWechat ? "wechat" : "qrcode");
    } else if (payMethod === "qrcode" && !showQrcode) {
      setPayMethod(showWechat ? "wechat" : "alipay");
    }
  }, [showWechat, showAlipay, showQrcode]);

  const handlePay = useCallback(() => {
    if (!isLoggedIn) {
      window.location.href = "/login?redirect=/vip";
      return;
    }
    setShowPayModal(true);
  }, [isLoggedIn]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <TealNavbar />

      <main className="pt-16">
        {/* VIP功能已关闭提示 */}
        {!enableVip && !loading && (
          <div className="w-full px-4 py-24">
            <div className="max-w-md mx-auto text-center">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-gray-100">
                <i className="ri-vip-crown-line text-4xl text-gray-400" />
              </div>
              <h1 className="text-xl font-bold text-gray-800 mb-3">VIP会员功能已关闭</h1>
              <p className="text-gray-500 mb-6">非常抱歉，VIP会员功能目前暂时关闭，如有需要请联系管理员。</p>
              <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors">
                <i className="ri-arrow-left-line" />
                返回首页
              </Link>
            </div>
          </div>
        )}

        {/* ── 主体内容 ── */}
        {enableVip && (
          <>
            {/* ── Hero 区：居中白色卡片式布局（vs 红色全宽渐变）── */}
            <section className="w-full px-4 md:px-8 pt-10 pb-6">
              <div className="max-w-4xl mx-auto">
                <div className="relative overflow-hidden bg-white rounded-3xl border border-gray-100 shadow-lg shadow-gray-200/40">
                  {/* 右侧装饰区域 - teal 色块 */}
                  <div className="absolute top-0 right-0 w-72 h-full bg-gradient-to-br from-teal-500/8 via-teal-400/5 to-emerald-400/8" />
                  <div className="absolute top-8 right-20 w-32 h-32 bg-teal-400/10 rounded-full blur-2xl" />
                  <div className="absolute bottom-6 right-40 w-24 h-24 bg-emerald-300/10 rounded-full blur-xl" />
                  
                  {/* 装饰图形 */}
                  <div className="absolute top-5 right-6 opacity-10">
                    <i className="ri-vip-crown-line text-8xl text-teal-600" />
                  </div>

                  <div className="relative z-10 p-8 md:p-12 lg:p-14">
                    <div className="flex flex-col md:flex-row items-start gap-10">
                      {/* 左侧文案 */}
                      <div className="flex-1">
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-50 text-teal-700 text-xs font-medium rounded-full mb-5 border border-teal-100">
                          <i className="ri-vip-crown-fill text-amber-500 text-sm" />
                          VIP 会员
                        </div>
                        <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight mb-4">
                          解锁<span className="text-teal-600">会员权益</span>
                          <br />
                          <span className="text-gray-500 text-2xl md:text-3xl lg:text-4xl font-semibold">让您的服务脱颖而出</span>
                        </h1>
                        <p className="text-gray-500 text-base md:text-lg max-w-lg leading-relaxed">
                          获取置顶曝光、专属认证和数据洞察，在竞争中领先一步
                        </p>
                      </div>

                      {/* 右侧数据点缀 */}
                      <div className="flex-shrink-0 flex flex-col gap-4">
                        <div className="flex items-center gap-3 bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl px-5 py-4 border border-teal-100/50">
                          <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
                            <i className="ri-user-star-line text-xl text-teal-600" />
                          </div>
                          <div>
                            <div className="text-lg font-bold text-teal-700">5000+</div>
                            <div className="text-xs text-gray-500">活跃会员</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 bg-gradient-to-br from-cyan-50 to-teal-50 rounded-2xl px-5 py-4 border border-cyan-100/50">
                          <div className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center">
                            <i className="ri-star-line text-xl text-cyan-600" />
                          </div>
                          <div>
                            <div className="text-lg font-bold text-cyan-700">98%</div>
                            <div className="text-xs text-gray-500">满意度</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* ── 权益展示区：3大横向模块（vs 红色6宫格卡片）── */}
            <section className="w-full px-4 md:px-8 pb-6">
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-8">
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                    VIP <span className="text-teal-600">专属权益</span>
                  </h2>
                  <p className="text-gray-400 text-sm">升级会员，享受更强大的功能与服务</p>
                </div>

                {/* 三大核心权益 - 横向大卡片 */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {VIP_HIGHLIGHTS.map((h, idx) => (
                    <div
                      key={h.title}
                      className={`group relative overflow-hidden rounded-2xl ${h.bgColor} p-6 border border-gray-100 hover:border-teal-200 hover:shadow-md transition-all duration-300`}
                    >
                      {/* 顶部渐变圆 */}
                      <div className={`w-14 h-14 mb-4 rounded-2xl bg-gradient-to-br ${h.color} flex items-center justify-center text-white shadow-lg`}>
                        <i className={`${h.icon} text-2xl`} />
                      </div>
                      <h3 className="text-lg font-bold text-gray-800 mb-2">{h.title}</h3>
                      <p className="text-sm text-gray-500 leading-relaxed">{h.desc}</p>
                    </div>
                  ))}
                </div>

                {/* 额外扩展权益标签 */}
                <div className="flex flex-wrap justify-center gap-3 mt-5">
                  {SUB_BENEFITS.map((s) => (
                    <div
                      key={s.label}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-gray-200 text-sm text-gray-500 hover:border-teal-300 hover:text-teal-600 transition-colors"
                    >
                      <i className={`${s.icon} text-teal-500`} />
                      {s.label}
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* ── 套餐选择区：Tab切换单卡展示（vs 红色3栏并排）── */}
            <section className="w-full px-4 md:px-8 pb-12">
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-8">
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                    选择适合您的<span className="text-teal-600">套餐</span>
                  </h2>
                  <p className="text-gray-400 text-sm">选择最适合您的会员方案，解锁更多权益</p>
                </div>

                {loading ? (
                  <div className="text-center py-16">
                    <div className="w-10 h-10 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-gray-500">加载中...</p>
                  </div>
                ) : (
                  <>
                    {/* Tab 切换栏 */}
                    <div className="flex justify-center mb-8">
                      <div className="inline-flex bg-gray-100 rounded-2xl p-1.5 gap-1">
                        {vipPlans.map((plan) => (
                          <button
                            key={plan.id}
                            onClick={() => setSelectedPlan(plan.id)}
                            className={`relative px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
                              selectedPlan === plan.id
                                ? "bg-white text-teal-700 shadow-md shadow-gray-200/60"
                                : "text-gray-500 hover:text-gray-700"
                            }`}
                          >
                            {plan.name}
                            {plan.badge && (
                              <span className="ml-1.5 px-1.5 py-0.5 bg-teal-100 text-teal-600 rounded text-xs">
                                {plan.badge}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* 套餐详情卡 - 单卡大展示 */}
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-lg shadow-gray-200/40 overflow-hidden">
                      <div className="p-8 md:p-10">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                          {/* 左侧：价格 */}
                          <div className="flex-shrink-0 text-center md:text-left">
                            <p className="text-sm text-gray-400 mb-1">{currentPlan?.name}</p>
                            <div className="flex items-baseline gap-1 justify-center md:justify-start">
                              <span className="text-gray-400">¥</span>
                              <span className="text-5xl font-extrabold text-teal-600">{currentPlan?.price}</span>
                            </div>
                            <p className="text-sm text-gray-400 mt-1">/{currentPlan?.duration}</p>
                            {currentPlan && currentPlan.originalPrice > currentPlan.price && (
                              <p className="text-sm mt-2">
                                <span className="line-through text-gray-300">¥{currentPlan.originalPrice}</span>
                                <span className="ml-2 px-2 py-0.5 bg-teal-50 text-teal-600 rounded text-xs font-medium">
                                  省¥{currentPlan.originalPrice - currentPlan.price}
                                </span>
                              </p>
                            )}
                          </div>

                          {/* 中间：分割线 */}
                          <div className="hidden md:block w-px h-24 bg-gray-100" />
                          <div className="md:hidden border-t border-gray-100" />

                          {/* 右侧：特性列表 */}
                          <div className="flex-1 md:pl-6">
                            <p className="text-sm font-semibold text-gray-700 mb-3">套餐包含权益</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
                              {currentPlan?.features.map((f, fi) => (
                                <div key={fi} className="flex items-center gap-2.5">
                                  <div className="w-5 h-5 rounded-full bg-teal-50 flex items-center justify-center shrink-0">
                                    <i className="ri-check-line text-teal-500 text-xs" />
                                  </div>
                                  <span className="text-sm text-gray-600">{f}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* 支付方式 */}
                        <div className="mt-8 pt-6 border-t border-gray-100">
                          <p className="text-sm font-semibold text-gray-700 mb-3">选择支付方式</p>
                          <div className="flex gap-3 flex-wrap">
                            {showQrcode && (
                              <button
                                onClick={() => setPayMethod("qrcode")}
                                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 text-sm font-medium transition-all ${
                                  payMethod === "qrcode"
                                    ? "border-teal-500 bg-teal-50 text-teal-700"
                                    : "border-gray-200 text-gray-500 hover:border-gray-300"
                                }`}
                              >
                                <i className="ri-qr-code-line text-lg" />
                                收款码支付
                              </button>
                            )}
                            {showWechat && (
                              <button
                                onClick={() => setPayMethod("wechat")}
                                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 text-sm font-medium transition-all ${
                                  payMethod === "wechat"
                                    ? "border-green-500 bg-green-50 text-green-700"
                                    : "border-gray-200 text-gray-500 hover:border-gray-300"
                                }`}
                              >
                                <i className="ri-wechat-pay-fill text-lg" />
                                微信支付
                              </button>
                            )}
                            {showAlipay && (
                              <button
                                onClick={() => setPayMethod("alipay")}
                                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 text-sm font-medium transition-all ${
                                  payMethod === "alipay"
                                    ? "border-teal-500 bg-teal-50 text-teal-700"
                                    : "border-gray-200 text-gray-500 hover:border-gray-300"
                                }`}
                              >
                                <i className="ri-alipay-fill text-lg" />
                                支付宝
                              </button>
                            )}
                          </div>
                        </div>

                        {/* CTA按钮 */}
                        <div className="mt-6 text-center">
                          <button
                            onClick={handlePay}
                            className="inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-teal-600 to-emerald-600 text-white font-bold rounded-2xl hover:from-teal-700 hover:to-emerald-700 transition-all text-base shadow-xl shadow-teal-500/25 hover:shadow-teal-500/40 hover:-translate-y-0.5"
                          >
                            <i className="ri-vip-crown-line text-xl" />
                            <span>
                              立即开通 — ¥{currentPlan?.price}
                              {currentPlan && currentPlan.originalPrice > currentPlan.price && (
                                <span className="ml-2 text-white/60 text-xs line-through">¥{currentPlan.originalPrice}</span>
                              )}
                            </span>
                          </button>
                          <p className="text-xs text-gray-400 mt-3">
                            开通即享全部VIP权益，随时可取消
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </section>
          </>
        )}
      </main>

      {enableVip && <TealFooter />}

      {/* ── 支付弹窗 ── */}
      {enableVip && showPayModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-gray-800 text-lg">扫码支付</h3>
              <button
                onClick={() => setShowPayModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              >
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>

            <div className="text-center">
              {payMethod === "alipay" && showAlipay && (
                <div>
                  <p className="text-xs text-gray-500 mb-3">请使用支付宝扫码付款</p>
                  <img
                    src={paymentConfig?.alipay_qrcode}
                    alt="支付宝收款码"
                    className="w-52 h-52 mx-auto rounded-xl border border-gray-200"
                  />
                </div>
              )}

              {payMethod === "wechat" && showWechat && (
                <div>
                  <p className="text-xs text-gray-500 mb-3">请使用微信扫码付款</p>
                  <img
                    src={paymentConfig?.wechat_qrcode}
                    alt="微信收款码"
                    className="w-52 h-52 mx-auto rounded-xl border border-gray-200"
                  />
                </div>
              )}

              {payMethod === "qrcode" && (
                <div className="flex flex-col items-center gap-4">
                  {showAlipay && (
                    <div>
                      <p className="text-xs text-gray-500 mb-2">支付宝扫码付款</p>
                      <img
                        src={paymentConfig?.alipay_qrcode}
                        alt="支付宝收款码"
                        className="w-40 h-40 mx-auto rounded-xl border border-gray-200"
                      />
                    </div>
                  )}
                  {showWechat && (
                    <div>
                      <p className="text-xs text-gray-500 mb-2">微信扫码付款</p>
                      <img
                        src={paymentConfig?.wechat_qrcode}
                        alt="微信收款码"
                        className="w-40 h-40 mx-auto rounded-xl border border-gray-200"
                      />
                    </div>
                  )}
                </div>
              )}

              {!showAlipay && !showWechat && (
                <>
                  <div className="w-44 h-44 mx-auto mb-4 bg-gray-100 rounded-xl flex items-center justify-center">
                    <i className="ri-qr-code-line text-5xl text-gray-300" />
                  </div>
                  <p className="text-sm text-gray-600 mb-1">支付功能暂未启用</p>
                </>
              )}

              <p className="text-2xl font-bold text-teal-600 mt-4 mb-1">¥{currentPlan?.price}</p>
              <p className="text-xs text-gray-400">{currentPlan?.name} · {currentPlan?.duration}</p>
            </div>

            <div className="mt-5 p-3.5 bg-teal-50 rounded-xl text-xs text-teal-700 text-center font-medium border border-teal-100">
              <i className="ri-information-line mr-1" />
              支付成功后将自动开通VIP权益
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
